import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';
import { ElementProps } from '@/components/sections/builder/types';
import { ProductReviewElementDataType } from '@/components/sections/builder/elements/body/product-review/type';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import { DEFAULT_THEME_COLOR_SCHEME } from '@/components/sections/builder/theme/color';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import { Switch } from '@/components/ui/switch';
import InputSelect from '@/components/sections/builder/settings/input-select';
import { Separator } from '@/components/ui/separator';
import { SORTING_QUERY } from '@/components/sections/builder/elements/body/product-review/component/filter-product-review';

export default function ElementSettingProductReview({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ProductReviewElementDataType;

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'> Header Settings</h1>
      <div className='mb-2 mt-4 '>Heading label</div>
      <TextEditorMinimal
        placeholder='Label'
        onChange={(value) =>
          builder.setElementData(element.id, {
            headingLabel: value,
          })
        }
        value={data.headingLabel}
        className='mb-4 w-full '
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='grid grid-cols-2 items-center gap-2 '>
        <div className='mb-4 '>Heading alignment</div>
        <InputSelect
          defaultValue={data.contentAlign}
          options={[
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { contentAlign: v })}
        />
      </div>
      <div className='mt-4 grid grid-cols-2 items-center gap-2'>
        <div className='mb-4 mt-4 '>Heading size</div>
        <InputSelect
          defaultValue={data.headingSize}
          options={[
            { label: 'Small', value: 'small' },
            { label: 'Medium', value: 'medium' },
            { label: 'Large', value: 'large' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              headingSize: v,
            })
          }
        />
      </div>
      <Separator className='my-4' />
      <h1 className='mb-3 text-sm font-bold'> Style</h1>
      <div className='mb-2 '>Color scheme</div>
      <SchemeSetting
        value={data.colorScheme || DEFAULT_THEME_COLOR_SCHEME}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            colorScheme: theme,
          });
        }}
      />
      <div className='mb-4 mt-1 text-xs'>
        To edit all your theme&apos;s colors, go to your color theme settings.
      </div>
      <div className='my-2 flex items-center gap-2'>
        <label>Enable sorting</label>
        <Switch
          className='ml-auto'
          checked={data.showSorting}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showSorting: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='my-2 flex items-center gap-2'>
        <label>Enable filtering</label>
        <Switch
          className='ml-auto'
          checked={data.showFilter}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showFilter: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mb-2 mt-4 '>Reviews per page</div>
      <InputSlider
        value={data.reviewsPerPage || 5}
        max={25}
        onChange={(e) =>
          builder.setElementData(element.id, {
            reviewsPerPage: e,
          })
        }
      />
      <div className='mt-4 grid grid-cols-2 items-center gap-2'>
        <div className='mb-4 mt-4 '>Default Sort By</div>
        <InputSelect
          defaultValue={data.defaultSortBy || 'created-descending'}
          options={SORTING_QUERY.map((item) => ({
            label: item.label,
            value: item.value,
          }))}
          onChange={(v) =>
            builder.setElementData(element.id, {
              defaultSortBy: v,
            })
          }
        />
      </div>
    </div>
  );
}
